import { isExpired, sessionDays, sessionDeadline } from './sessionPolicy';

const DATABASE = 'lunadesk-safe-device-sessions-v1';
const TABLE = 'sessions';
const EVENT_KEY = 'lunadesk.safe.session.revoke.v1';
const memory = new Map(), listeners = new Set(), localStamps = new Map();
let channel, listening = false, queue = Promise.resolve();
const encode = value => new TextEncoder().encode(JSON.stringify(value));
const stampKey = owner => `lunadesk.safe.session.stamp.${owner}`;
const meta = record => ({ version: 1, owner: record.owner, vaultId: record.vaultId, startedAt: record.startedAt, expiresAt: record.expiresAt, days: record.days, stamp: record.stamp, recordId: record.recordId });
function stamp(owner) {
  try { return `${localStorage.getItem(stampKey('*')) || ''}:${localStorage.getItem(stampKey(owner)) || ''}`; }
  catch { return `${localStamps.get('*') || ''}:${localStamps.get(owner) || ''}`; }
}
function wipeMemory(owner) {
  for (const [id, value] of memory) if (owner === '*' || id === owner) {
    clearTimeout(value.timer); value.key.fill(0); memory.delete(id);
  }
}
function received(event) {
  if (!event || typeof event.owner !== 'string') return;
  if (event.nonce) localStamps.set(event.owner, event.nonce);
  wipeMemory(event.owner);
  listeners.forEach(listener => listener(event.owner, event.reason || 'manual'));
}
function listen() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('storage', event => {
    if (event.key === EVENT_KEY && event.newValue) { try { received(JSON.parse(event.newValue)); } catch { /* Invalid external preference. */ } }
    if (event.key === null) received({ owner: '*', reason: 'cleared' });
  });
  window.addEventListener('focus', () => {
    for (const [owner, entry] of memory) if (isExpired(entry) || entry.stamp !== stamp(owner)) {
      void forgetSession(owner, 'expired');
    }
  });
  try { channel = new BroadcastChannel(EVENT_KEY); channel.onmessage = event => received(event.data); } catch { /* Storage events remain available. */ }
}
function db() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(Error('IndexedDB недоступна.')); return; }
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(TABLE, { keyPath: 'owner' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(Error('Хранилище устройства заблокировано другой вкладкой.'));
  });
}
async function transaction(mode, action) {
  const database = await db();
  try {
    return await new Promise((resolve, reject) => {
      const tx = database.transaction(TABLE, mode), request = action(tx.objectStore(TABLE));
      let result;
      if (request) request.onsuccess = () => { result = request.result; };
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error || request?.error);
      tx.onabort = () => reject(tx.error || Error('Запись сессии прервана.'));
    });
  } finally { database.close(); }
}
async function removeRevoked(owner) {
  const database = await db();
  try {
    await new Promise((resolve, reject) => {
      const tx = database.transaction(TABLE, 'readwrite'), store = tx.objectStore(TABLE);
      const request = owner === '*' ? store.openCursor() : store.get(owner);
      request.onsuccess = () => {
        if (owner === '*') {
          const cursor = request.result;
          if (cursor) { if (cursor.value.stamp !== stamp(cursor.value.owner)) cursor.delete(); cursor.continue(); }
        } else if (request.result && request.result.stamp !== stamp(owner)) store.delete(owner);
      };
      tx.oncomplete = resolve; tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
    });
  } finally { database.close(); }
}
async function removeWritten(owner, recordId) {
  const database = await db();
  try {
    await new Promise((resolve, reject) => {
      const tx = database.transaction(TABLE, 'readwrite'), store = tx.objectStore(TABLE), request = store.get(owner);
      request.onsuccess = () => { if (request.result?.recordId === recordId) store.delete(owner); };
      tx.oncomplete = resolve; tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
    });
  } finally { database.close(); }
}
function serial(action) { const result = queue.catch(() => {}).then(action); queue = result.catch(() => {}); return result; }
function putMemory(owner, entry) {
  wipeMemory(owner);
  const saved = { ...entry, key: entry.key.slice(), timer: null };
  if (saved.expiresAt !== null) saved.timer = setTimeout(() => {
    void forgetSession(owner, 'expired');
  }, Math.max(0, saved.expiresAt - Date.now()));
  memory.set(owner, saved);
}
export function sessionInfo(owner) {
  const saved = memory.get(owner);
  return saved ? { startedAt: saved.startedAt, expiresAt: saved.expiresAt, days: saved.days, persistent: saved.persistent } : null;
}
export function subscribeSessionLock(listener) { listen(); listeners.add(listener); return () => listeners.delete(listener); }

export function forgetSession(owner = '*', reason = 'manual') {
  listen(); wipeMemory(owner);
  listeners.forEach(listener => listener(owner, reason));
  const nonce = crypto.randomUUID(); localStamps.set(owner, nonce);
  const event = { owner, nonce, reason };
  try { localStorage.setItem(stampKey(owner), nonce); localStorage.setItem(EVENT_KEY, JSON.stringify(event)); } catch { /* Memory-only revocation still works. */ }
  try { channel?.postMessage(event); } catch { /* Other tabs also observe storage. */ }
  return serial(() => removeRevoked(owner)).catch(() => {});
}

export async function rememberSession(owner, session, preferences, { startedAt = Date.now() } = {}) {
  listen();
  if (!owner || !(session.key instanceof Uint8Array) || session.key.length !== 32) throw Error('Неверная сессия Safe.');
  const days = sessionDays(preferences.sessionDays), token = stamp(owner);
  const info = { recordId: crypto.randomUUID(), owner, vaultId: session.envelope.id, key: session.key.slice(), startedAt, expiresAt: sessionDeadline(startedAt, days), days, stamp: token, persistent: preferences.rememberDevice === true };
  if (isExpired(info)) { info.key.fill(0); await forgetSession(owner, 'expired'); return null; }
  putMemory(owner, info);
  try {
    await serial(async () => {
      if (stamp(owner) !== token) return;
      if (!info.persistent) { await transaction('readwrite', store => store.delete(owner)).catch(() => {}); return; }
      const deviceKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: encode(meta(info)) }, deviceKey, info.key);
      if (stamp(owner) !== token) return;
      await transaction('readwrite', store => store.put({ ...meta(info), deviceKey, iv, ciphertext }));
      if (stamp(owner) !== token) await removeWritten(owner, info.recordId);
    });
    return sessionInfo(owner);
  } catch {
    const active = memory.get(owner);
    if (active && active.stamp === token) active.persistent = false;
    return { ...sessionInfo(owner), persistent: false, storageFailed: info.persistent };
  } finally { info.key.fill(0); }
}

export async function restoreSessionKey(owner, vaultId, allowPersistent) {
  listen();
  const token = stamp(owner), active = memory.get(owner);
  if (active) {
    if (active.vaultId === vaultId && active.stamp === token && !isExpired(active)) return { key: active.key.slice(), info: sessionInfo(owner) };
    await forgetSession(owner, 'expired'); return null;
  }
  if (!allowPersistent) return null;
  let record, key;
  try {
    record = await serial(() => transaction('readonly', store => store.get(owner)));
    if (!record) return null;
    if (record.version !== 1 || typeof record.recordId !== 'string' || record.owner !== owner || record.vaultId !== vaultId || record.stamp !== token || isExpired(record) || !Number.isFinite(record.startedAt) || record.days !== sessionDays(record.days) || record.expiresAt !== sessionDeadline(record.startedAt, record.days)) {
      await forgetSession(owner, 'expired'); return null;
    }
    if (!record.deviceKey || record.deviceKey.extractable !== false || record.deviceKey.algorithm.name !== 'AES-GCM') throw Error('Неверный ключ устройства.');
    key = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.iv, additionalData: encode(meta(record)) }, record.deviceKey, record.ciphertext));
    if (key.length !== 32 || stamp(owner) !== token || isExpired(record)) { key.fill(0); return null; }
    putMemory(owner, { ...meta(record), key, persistent: true });
    return { key, info: sessionInfo(owner) };
  } catch {
    key?.fill(0); await forgetSession(owner, 'corrupt'); return null;
  }
}
